import { useState } from 'react';
import { Button, InputBoxSingle } from '@peacecover/component';
import type { BettieSettingStep } from '../BettieSettingPage';
import type { components } from '@pocapan/shared/types/schema';
import { useBettieSearch } from '@pocapan/shared/domains/bettie/ui/hooks/useBettieSearch';
import type { BettieSettingInfo } from '../../hooks/useBettieSettingInfo';
import { useVirtualKeyboardVisible } from '../hooks/useVirtualKeyboardVisible';
import { t } from 'i18next';
import Bubble from '../../components/floating/Bubble';

type ApiV1MemberPersona = components['schemas']['ApiV1MemberPersona'];

interface BettiePersonaSuggestionProps {
  bettieSettingInfo: BettieSettingInfo;
  updateBettieSettingInfo: (partial: Partial<BettieSettingInfo>) => void;
  setCurrentStep: (currentStep: BettieSettingStep) => void;
  setBettiePersona: (bettiePersona: ApiV1MemberPersona) => void;
}

const BettiePersonaSuggestion = ({
  bettieSettingInfo,
  updateBettieSettingInfo,
  setCurrentStep,
  setBettiePersona
}: BettiePersonaSuggestionProps) => {
  const [searchInput, setSearchInput] = useState('');
  const bettieSearchResult = useBettieSearch(
    searchInput.trim() || bettieSettingInfo.bettieNickname
  );
  const { isKeyboardVisible, handleFocus, handleBlur } = useVirtualKeyboardVisible();

  const handleButtonClick = async () => {
    const { data } = await bettieSearchResult.refetch();
    if (!data) {
      return;
    }
    setBettiePersona(data.bettie);
    updateBettieSettingInfo({
      memberPersonaId: data.bettie.memberPersonaId
    });

    setCurrentStep('loading');
  };
  return (
    <div className="fixed inset-0 min-h-screen flex flex-col overflow-y-auto pt-[calc(env(safe-area-inset-top)+55px)] pb-[env(safe-area-inset-bottom)]">
      <div className="flex flex-col items-start px-[24px] pb-[10px] mt-[106px]">
        <Bubble
          text={t('BettieSetting.personaSuggestion.tooltipDescription')}
          gradientDirection="left"
          className="mb-[18px]"
        />
        <h1 className="text-[26px] font-bold text-text-main-lv1 leading-[31px] text-left whitespace-pre-line">
          {t('BettieSetting.personaSuggestion.title')}
        </h1>
        <p className="text-[15px] font-medium text-text-main-lv4 leading-[18px] mt-[14px] whitespace-pre-line">
          {t('BettieSetting.personaSuggestion.description')}
        </p>
      </div>
      <div className="w-full px-[24px] mt-[22px]">
        <InputBoxSingle
          className="bg-transparent border-none"
          placeholder={t('BettieSetting.personaSuggestion.placeholder') as string}
          value={searchInput}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onChange={(e) => setSearchInput(e.target.value)}
        />
      </div>
      <div
        className={`fixed bottom-[calc(10px+env(safe-area-inset-bottom))] flex flex-col items-center w-full px-[24px] pb-[10px] transition-all duration-150 ease-in-out ${isKeyboardVisible ? 'translate-y-full opacity-0' : 'translate-y-0 opacity-100'}`}
      >
        <Button
          size="large"
          className="w-full text-[17px] font-semibold"
          onClick={handleButtonClick}
        >
          {t('BettieSetting.personaSuggestion.button')}
        </Button>
      </div>
    </div>
  );
};

export default BettiePersonaSuggestion;
